import { useI18n } from "@core/i18n/i18n";
import { useNavigate } from "@core/routing/use-navigate";
import { patientManager } from "@core/service-container";
import { HealthcareTeamInvitationPatientData } from "@domain/healthcare-team/healthcare-team-manager";
import { PendingPatient } from "@domain/patient/patient";
import {
	sendAnalyticsEventCreateNewPatient,
	sendAnalyticsEventJoinExistingPatient,
	sendAnalyticsEventSharePatientCode,
} from "@domain/patient/patient-analytics";
import { AnalyticsTemplateLocation, sendAnalyticsEventCreateTemplate } from "@domain/template/template-analytics";
import styled from "@emotion/styled";
import { RoutesPath } from "@ui/navigation/routes";
import { CreatePendingPatientForm } from "@ui/pages/home/pending-patient-dialog/components/create-pending-patient-form";
import { HctInvitationCodeForm } from "@ui/pages/home/pending-patient-dialog/components/hct-invitation-code-form";
import { HealthcareTeamJoinedStep } from "@ui/pages/home/pending-patient-dialog/components/healthcare-team-joined-step";
import { InvitationCodeSent } from "@ui/pages/home/pending-patient-dialog/components/invitation-code-sent";
import { InviteOrCreatePatient } from "@ui/pages/home/pending-patient-dialog/components/invite-or-create-patient";
import {
	InvitationCodeSharingMethod,
	InvitationSentData,
	PendingPatientCreated,
} from "@ui/pages/home/pending-patient-dialog/components/pending-patient-created";
import { RegistrationDetailsStep } from "@ui/pages/home/pending-patient-dialog/components/registration-details-step";
import { stack } from "@ui/style";
import { isNil, isNotNil } from "@utils/assert";
import { DialogProps } from "primereact/dialog";
import React, { useCallback, useMemo, useState } from "react";
import { RestrictedDialog } from "@ui/components/dialog/restricted-dialog";
import { DIALOG_SIZE } from "@ui/components/dialog/base-dialog";

export interface AddPatientDialogProps extends DialogProps {
	pendingPatient?: PendingPatient;
	step?: DialogStep;
}

export enum DialogStep {
	InviteOrCreateChoice = "InviteOrCreateChoice",
	CreatePatientForm = "CreatePatientForm",
	RegistrationDetails = "RegistrationDetails",
	SendInvitationForm = "SendInvitationForm",
	InvitationConfirmed = "InvitationConfirmed",
	HealthcareTeamJoined = "HealthcareTeamJoined",
	InvitationCodeForm = "InvitationCodeForm",
}

export const AddPatientDialog: React.FC<AddPatientDialogProps> = ({ ...otherProps }) => {
	const { format } = useI18n();
	const navigate = useNavigate();
	const [pendingPatient, setPendingPatient] = useState<PendingPatient | undefined>(otherProps.pendingPatient);
	const [invitationSentData, setInvitationSentData] = useState<InvitationSentData>();
	const [healthcareTeamJoinedData, setHealthcareTeamJoinedData] = useState<
		HealthcareTeamInvitationPatientData | undefined
	>(undefined);
	const isNewlyCreatedPatient = useMemo(() => isNil(otherProps.pendingPatient), [otherProps.pendingPatient]);
	const [step, setStep] = useState<DialogStep>(
		() =>
			otherProps.step ?? (isNotNil(pendingPatient) ? DialogStep.SendInvitationForm : DialogStep.InviteOrCreateChoice),
	);

	const onPendingPatientCreated = useCallback((newPendingPatient: PendingPatient) => {
		setPendingPatient(newPendingPatient);
		setStep(DialogStep.SendInvitationForm);
	}, []);

	const onInvitationCodeSent = useCallback(
		(data: InvitationSentData) => {
			sendAnalyticsEventSharePatientCode(
				data.sharingMethod,
				isNewlyCreatedPatient,
				isNotNil(pendingPatient?.functionalStatus),
			);

			if (data.sharingMethod === InvitationCodeSharingMethod.PRINT && !isNewlyCreatedPatient) {
				otherProps.onHide();
			} else {
				setInvitationSentData(data);
				setStep(DialogStep.InvitationConfirmed);
			}
		},
		[isNewlyCreatedPatient, otherProps, pendingPatient?.functionalStatus],
	);

	const dialogHeader = useMemo(() => {
		switch (step) {
			case DialogStep.InviteOrCreateChoice:
				return format("home.addPatientDialog.inviteOrCreate.header");
			case DialogStep.InvitationCodeForm:
				return format("home.addPatientDialog.invitationCodeForm.header");
			case DialogStep.CreatePatientForm:
				return format("home.pendingPatientDialog.form.header.v2_6");
			case DialogStep.SendInvitationForm:
				return isNewlyCreatedPatient
					? format("home.pendingPatientDialog.form.header.v2_6")
					: format("home.pendingPatientDialog.resendCode.header");
			case DialogStep.InvitationConfirmed:
				return isNewlyCreatedPatient
					? format("home.pendingPatientDialog.codeSent.newPatient.header")
					: format("home.pendingPatientDialog.codeSent.resendCode.header");
			case DialogStep.RegistrationDetails:
				return format("home.pendingPatientDialog.registrationDetails.header");
			case DialogStep.HealthcareTeamJoined:
				return format("home.addPatientDialog.hctJoined.header");
		}
	}, [format, isNewlyCreatedPatient, step]);

	const onCreateNewTemplateRequest = useCallback(() => {
		sendAnalyticsEventCreateTemplate(AnalyticsTemplateLocation.PATIENT_CREATION);
		otherProps.onHide();
		navigate(RoutesPath.TemplateBuilder);
	}, [navigate, otherProps]);

	const renderStep = useCallback(() => {
		switch (step) {
			case DialogStep.InviteOrCreateChoice:
				return (
					<InviteOrCreatePatient
						createPatient={() => {
							sendAnalyticsEventCreateNewPatient();
							setStep(DialogStep.CreatePatientForm);
						}}
						joinHct={() => {
							sendAnalyticsEventJoinExistingPatient();
							setStep(DialogStep.InvitationCodeForm);
						}}
					/>
				);
			case DialogStep.CreatePatientForm:
				return (
					<CreatePendingPatientForm
						onPendingPatientCreated={onPendingPatientCreated}
						onBack={() => setStep(DialogStep.InviteOrCreateChoice)}
					/>
				);
			case DialogStep.SendInvitationForm:
				return isNotNil(pendingPatient) ? (
					<PendingPatientCreated
						pendingPatient={pendingPatient}
						onInvitationCodeSent={onInvitationCodeSent}
						previousInvitationSentData={invitationSentData}
					/>
				) : null;
			case DialogStep.InvitationConfirmed:
				return isNotNil(pendingPatient) && isNotNil(invitationSentData) ? (
					<InvitationCodeSent
						invitationSentData={invitationSentData}
						pendingPatient={pendingPatient}
						isNewlyCreatedPatient={isNewlyCreatedPatient}
						onPressRetry={() => setStep(DialogStep.SendInvitationForm)}
						onCreateNewTemplateRequest={onCreateNewTemplateRequest}
					/>
				) : null;
			case DialogStep.RegistrationDetails:
				return isNotNil(pendingPatient) ? (
					<RegistrationDetailsStep
						pendingPatient={pendingPatient}
						onResend={() => setStep(DialogStep.SendInvitationForm)}
						onCreateNewTemplateRequest={onCreateNewTemplateRequest}
					/>
				) : null;
			case DialogStep.InvitationCodeForm:
				return (
					<HctInvitationCodeForm
						onConfirm={async patientData => {
							setStep(DialogStep.HealthcareTeamJoined);
							setHealthcareTeamJoinedData(patientData);
							await patientManager.fetchAllPatients();
						}}
						onBack={() => setStep(DialogStep.InviteOrCreateChoice)}
					/>
				);
			case DialogStep.HealthcareTeamJoined:
				return isNotNil(healthcareTeamJoinedData) ? <HealthcareTeamJoinedStep {...healthcareTeamJoinedData} /> : null;
		}
	}, [
		healthcareTeamJoinedData,
		invitationSentData,
		isNewlyCreatedPatient,
		onCreateNewTemplateRequest,
		onInvitationCodeSent,
		onPendingPatientCreated,
		pendingPatient,
		step,
	]);

	return (
		<RestrictedDialog context="AddPatient" width={DIALOG_SIZE.S} header={dialogHeader} {...otherProps}>
			<DialogContainer>{renderStep()}</DialogContainer>
		</RestrictedDialog>
	);
};

const DialogContainer = styled.div`
	${stack("M", "flexstart", "stretch")};
	height: 100%;
`;
